import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/** Prefix v localStorage pre zapamätané zbalenie sekcií sidebaru. */
const LS_PREFIX = "tandem_sekcia_";

function readOpen(storageKey: string | undefined, fallback: boolean): boolean {
  if (!storageKey) return fallback;
  const v = localStorage.getItem(LS_PREFIX + storageKey);
  return v === null ? fallback : v === "1";
}

/**
 * Zabaliteľný box sidebaru — jednotný štýl pre Materiálové sloty,
 * Hrany v zákazke a Kovania v zákazke. `action` (napr. „+" tlačidlo)
 * sa renderuje vedľa toggle buttonu, nie v ňom (nested button je
 * invalid HTML).
 *
 * So `storageKey` si sekcia zapamätá zbalenie aj po reloade — bez neho sa
 * po každom otvorení zákazky vracia na `defaultOpen`.
 */
export function CollapsibleSection({
  title,
  action,
  defaultOpen = false,
  storageKey,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  defaultOpen?: boolean;
  /** Kľúč pre `localStorage`; bez neho sa stav nepamätá. */
  storageKey?: string;
  children: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(() => readOpen(storageKey, defaultOpen));
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(LS_PREFIX + storageKey, open ? "1" : "0");
    }
  }, [storageKey, open]);
  return (
    <section className="rounded-md border">
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex flex-1 items-center gap-1.5 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-accent transition-colors rounded-md"
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          )}
          {title}
        </button>
        {action ? <div className="pr-1.5 shrink-0">{action}</div> : null}
      </div>
      {open ? <div className="px-3 pb-2 space-y-1">{children}</div> : null}
    </section>
  );
}
